/**
 * A game in progress (docs/spec/02 §9): whose turn it is, lives, scores, which cave and level comes
 * next. Pure data and transitions; which cave a position means is the mode's `CaveSource`.
 */

import { SESSION } from '../sim/tuning';

export type Mode = 'classic' | 'deep' | 'expedition' | 'daily' | 'practice' | 'demo';
export type CaveOutcome = 'won' | 'died' | 'timeout' | 'quit';

export interface Player {
  readonly lives: number;
  readonly score: number;
  /** Position in the mode's sequence: the play-order index (Classic, Deep) or depth - 1 (Expedition). */
  readonly position: number;
  readonly level: number;
  readonly nextBonus: number;
  /** Caves attempted, for seeding each attempt differently. */
  readonly attempts: number;
}

export interface Session {
  readonly mode: Mode;
  readonly players: readonly Player[];
  readonly current: number;
  /** Lives never run out (Practice, Daily, the demo). */
  readonly unlimited: boolean;
  /** How many positions make a full round before the level goes up (20 in the sets, 0 = endless). */
  readonly roundLength: number;
}

export type Next =
  /** The same player plays the next cave. */
  | 'next'
  /** The same player plays the same cave again. */
  | 'retry'
  /** The other player's turn (their current cave). */
  | 'switch'
  /** The same player plays the next cave, and a level has just been completed. */
  | 'levelComplete'
  /** Nobody has lives left, or the player quit. */
  | 'gameOver'
  /** A one-cave mode is over (Daily, Practice win, the demo). */
  | 'finished';

export interface StartOptions {
  mode: Mode;
  players?: 1 | 2;
  position?: number;
  level?: number;
}

export function startSession(o: StartOptions): Session {
  const unlimited = o.mode === 'practice' || o.mode === 'daily' || o.mode === 'demo';
  const player: Player = {
    lives: SESSION.startLives,
    score: 0,
    position: o.position ?? 0,
    level: Math.min(5, Math.max(1, o.level ?? 1)),
    nextBonus: SESSION.bonusLifeEvery,
    attempts: 0,
  };
  const count = o.mode === 'classic' || o.mode === 'deep' ? (o.players ?? 1) : 1;
  return {
    mode: o.mode,
    players: Array.from({ length: count }, () => player),
    current: 0,
    unlimited,
    roundLength: o.mode === 'classic' || o.mode === 'deep' ? 20 : 0,
  };
}

const NO_PLAYER: Player = { lives: 0, score: 0, position: 0, level: 1, nextBonus: SESSION.bonusLifeEvery, attempts: 0 };

export function currentPlayer(s: Session): Player {
  return s.players[s.current] ?? s.players[0] ?? NO_PLAYER;
}

function withPlayer(s: Session, p: Player, index = s.current): Session {
  return { ...s, players: s.players.map((q, i) => (i === index ? p : q)) };
}

/** Points scored mid-cave: returns the new session and how many bonus lives they earned. */
export function addScore(s: Session, points: number): { session: Session; bonusLives: number } {
  let p = currentPlayer(s);
  let bonusLives = 0;
  let { score, nextBonus, lives } = p;
  score += Math.max(0, points);
  while (score >= nextBonus) {
    nextBonus += SESSION.bonusLifeEvery;
    bonusLives++;
    lives = Math.min(SESSION.maxLives, lives + 1);
  }
  p = { ...p, score, nextBonus, lives };
  return { session: withPlayer(s, p), bonusLives };
}

/** The next player with lives, after `from`; -1 if none. */
function nextAlive(s: Session, from: number): number {
  for (let k = 1; k <= s.players.length; k++) {
    const i = (from + k) % s.players.length;
    if ((s.players[i]?.lives ?? 0) > 0) return i;
  }
  return -1;
}

/** Move the player on one position; a completed round raises the level (5 stays 5). */
function advance(s: Session, p: Player): { player: Player; levelUp: boolean } {
  const position = p.position + 1;
  if (s.roundLength > 0 && position >= s.roundLength) {
    return { player: { ...p, position: 0, level: Math.min(5, p.level + 1) }, levelUp: true };
  }
  return { player: { ...p, position }, levelUp: false };
}

/**
 * A cave has ended. `intermission` says whether it was a bonus cave (no life lost, a life for
 * finishing it). Returns the new session and what happens next.
 */
export function endCave(s: Session, outcome: CaveOutcome, intermission: boolean): { session: Session; next: Next } {
  const p0 = currentPlayer(s);
  const p = { ...p0, attempts: p0.attempts + 1 };
  if (outcome === 'quit') return { session: withPlayer(s, p), next: 'gameOver' };
  if (s.mode === 'daily' || s.mode === 'demo') return { session: withPlayer(s, p), next: 'finished' };
  if (s.mode === 'practice') return { session: withPlayer(s, p), next: outcome === 'won' ? 'finished' : 'retry' };

  if (outcome === 'won') {
    const bonus = intermission ? 1 : 0;
    const { player, levelUp } = advance(s, { ...p, lives: Math.min(SESSION.maxLives, p.lives + bonus) });
    return { session: withPlayer(s, player), next: levelUp ? 'levelComplete' : 'next' };
  }
  if (intermission) {
    // A failed bonus cave costs nothing; in a two-player game the turn still passes.
    const { player } = advance(s, p);
    const moved = withPlayer(s, player);
    const other = s.players.length > 1 ? nextAlive(moved, s.current) : s.current;
    if (other >= 0 && other !== s.current) return { session: { ...moved, current: other }, next: 'switch' };
    return { session: moved, next: 'next' };
  }
  const lost = withPlayer(s, { ...p, lives: Math.max(0, p.lives - 1) });
  const who = nextAlive(lost, s.current);
  if (who < 0) return { session: lost, next: 'gameOver' };
  if (who !== s.current) return { session: { ...lost, current: who }, next: 'switch' };
  return { session: lost, next: 'retry' };
}

/** A seed for the current attempt, so each try at a cave rolls its dice afresh. */
export function attemptSeed(s: Session, salt: number): number {
  const p = currentPlayer(s);
  return (Math.imul(salt, 0x9e3779b1) ^ Math.imul(p.attempts + 1, 40503) ^ (s.current * 97) ^ (p.position * 131) ^ (p.level * 7919)) >>> 0;
}
